import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel } from './FieldLabel';
import { ProductPicker, type ProductSelection } from './ProductPicker';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import {
  ActiveToggle,
  FormActions,
  GroupField,
  LockedValue,
  PRODUCT_COUNTRIES,
  TITLE_MAX_LENGTH,
  ValidationBanner,
  type CodeFormProps,
} from './qrCodeFormParts';

const MAX_KITE_NUMBERS_IN_LINK = 20;

/**
 * A product code names itself: nobody types a title for a label that already has a product on it, and a
 * typed one drifts the moment the code is repointed at a re-coded product.
 */
export function productCodeTitle(product: ProductSelection): string {
  return [product.productName, product.rouCode, ...product.kiteProductNumbers]
    .map((part) => part?.trim())
    .filter((part) => Boolean(part))
    .join(' · ')
    .slice(0, TITLE_MAX_LENGTH);
}

/**
 * The link a product code will carry. It mirrors ProductPageUrlBuilder on the API, which builds the value
 * that is actually stored - this is only here so the form can show it before the code exists. Both are
 * pinned to the same shape by tests on their own side.
 */
export function productPageUrl(baseUrl: string, product: ProductSelection): string {
  const path = `${baseUrl.replace(/\/+$/, '')}/p/${encodeURIComponent(product.countryCode.toLowerCase())}/${encodeURIComponent(product.rouCode)}`;
  const query = [...new Set(product.kiteProductNumbers.filter(Boolean).map((number) => number.trim()))]
    .slice(0, MAX_KITE_NUMBERS_IN_LINK)
    .map((number) => `k=${encodeURIComponent(number)}`)
    .join('&');
  return query ? `${path}?${query}` : path;
}

/** A code printed on packaging. Its title and its destination both come from the product it names. */
export function ProductCodeForm({
  groups,
  qrCode,
  submitting = false,
  onSubmit,
  onCancel,
  kindSwitch,
}: CodeFormProps) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const isEditing = qrCode !== undefined;
  const editableGroups = groups.filter((group) => group.canEditQrCodes);

  const [ownerGroupId, setOwnerGroupId] = useState(qrCode?.ownerGroupId ?? editableGroups[0]?.id ?? '');
  const [productCountry, setProductCountry] = useState(qrCode?.productCountryCode ?? PRODUCT_COUNTRIES[0]);
  const [product, setProduct] = useState<ProductSelection | null>(
    qrCode?.productRouCode
      ? {
          rouCode: qrCode.productRouCode,
          countryCode: qrCode.productCountryCode ?? PRODUCT_COUNTRIES[0],
          productName: qrCode.productName ?? qrCode.productRouCode,
          kiteProductNumbers: qrCode.productKiteNumbers,
        }
      : null,
  );
  const [isActive, setIsActive] = useState(qrCode?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const title = product === null ? '' : productCodeTitle(product);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!isEditing && !ownerGroupId) {
      setValidationError(t('qrCode.validation.groupRequired'));
      return;
    }

    if (product === null) {
      setValidationError(t('qrCode.validation.productRequired'));
      return;
    }

    setValidationError(null);

    await onSubmit({
      ownerGroupId,
      title,
      kind: 'Product',
      targetUrl: undefined,
      product,
      slug: undefined,
      isActive,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-label={isEditing ? t('qrCode.formLabelEdit') : t('qrCode.formLabelCreate')}
    >
      <ValidationBanner message={validationError} />

      <GroupField
        groups={editableGroups}
        qrCode={qrCode}
        value={ownerGroupId}
        disabled={submitting}
        onChange={setOwnerGroupId}
        kindSwitch={kindSwitch}
      />

      <div className="field-row">
        <div className="field">
          <FieldLabel htmlFor="qr-product-country" icon="pi-globe" required>
            {t('qrCode.productCountry')}
          </FieldLabel>
          <select
            id="qr-product-country"
            className="form-select"
            value={productCountry}
            disabled={submitting || product !== null}
            onChange={(event) => setProductCountry(event.target.value)}
          >
            {PRODUCT_COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {t(`qrCode.country.${code}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="field-row__grow">
          <ProductPicker
            selection={product}
            countryCode={productCountry}
            onChange={setProduct}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="field">
        <FieldLabel htmlFor="qr-title" icon="pi-tag">
          {t('qrCode.title')}
        </FieldLabel>
        <input
          id="qr-title"
          type="text"
          className="form-control"
          value={title}
          placeholder={t('qrCode.titleFromProduct')}
          disabled
          readOnly
        />
        <span className="hint">{t('qrCode.titleFromProduct')}</span>
      </div>

      {product !== null && (
        <div className="field">
          <FieldLabel htmlFor="qr-product-link" icon="pi-link">
            {t('qrCode.destinationUrl')}
          </FieldLabel>
          <input
            id="qr-product-link"
            type="text"
            className="form-control"
            value={productPageUrl(user?.productPageBaseUrl ?? '', product)}
            disabled
            readOnly
          />
          <span className="hint">{t('qrCode.destinationFromProduct')}</span>
        </div>
      )}

      {isEditing && (
        <>
          <LockedValue
            label={t('qrCode.linkId')}
            icon="pi-hashtag"
            value={qrCode.slug}
            mono
            copyable
            hint={t('qrCode.linkIdLockedHint')}
          />
          <ActiveToggle checked={isActive} disabled={submitting} onChange={setIsActive} />
        </>
      )}

      <FormActions submitting={submitting} isEditing={isEditing} onCancel={onCancel} />
    </form>
  );
}
